import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { AuthenticationService } from '../_services';
import { OperationStateService } from '../_services/operation-state.service';
import { ErrorHandlerService } from '../_services/error-handler.service';
import { ExecutionState } from '../../../assets/data/enum/executionState';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
  private externalProviderList = ['https://sigpac.mapama.gob.es'];
  private operationProgressWhiteList = ['api/equipmentTelemetry/getEquipmentLocations'];
  private hasError = false;

  constructor(
    private authenticationService: AuthenticationService,
    private operationStateService: OperationStateService,
    private errorHandlerService: ErrorHandlerService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Controlla se la richiesta deve essere esclusa dall'interceptor
    if (request.headers.has('X-Skip-Interceptor')) {
      const headers = request.headers.delete('X-Skip-Interceptor');
      return next.handle(request.clone({ headers }));
    }

    const tmpUrl: Request = request as any;
    if (tmpUrl.method === 'POST' || tmpUrl.method === 'PUT' || tmpUrl.method === 'DELETE') {
      // Gestione stato operazione in corso
      if (!this.isUrlOperationProgressWhiteList(request.url)) {
        this.operationStateService.showOperationInProgress$.next(ExecutionState.loading);
      }
    }

    // Gestione provider esterni
    if (this.isUrlExternalProvider(request.url)) {
      return next.handle(request);
    }

    // Aggiunta token di autenticazione
    const authToken = this.authenticationService.getAuthToken();
    if (authToken && this.authenticationService.isAuthTokenValide()) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken.access_token}`,
          // add auth header for common if needed
          ...(request.url.includes('common') ? { 'x-api-key': environment.commonApiKey } : {}),
        },
      });
    }

    // Aggiunta parametri e header
    request = this.addParamsToRequest(request);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.hasError = true;
        this.errorHandlerService.handleError(error);
        return throwError(error);
      }),
      finalize(() => {
        if (request.method === 'POST') {
          this.catchSuccessResponse(request.url);
        }
      })
    );
  }

  private isUrlExternalProvider(url: string): boolean {
    return this.externalProviderList.some((ep) => url.includes(ep));
  }

  private isUrlOperationProgressWhiteList(url: string) {
    let isOPWL = false;
    this.operationProgressWhiteList.forEach((ep) => {
      if (url.includes(ep)) {
        isOPWL = true;
      }
    });
    return isOPWL;
  }

  private addParamsToRequest(request: HttpRequest<any>): HttpRequest<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected') || '{}');
    let { params } = request;

    if (companySelected.id) {
      params = params.set('companyIds', companySelected.id);
    }

    params = params.set('multiFarm', String(this.operationStateService.themeConfig.multifarm));

    if (request.url.includes('api/private/v1/finance/') && companySelected.id) {
      params = params.set('tenantIds', companySelected.id);
    }

    const crntLang = localStorage.getItem('crntLng') || this.operationStateService.getLanguage();
    params = params.set('lang', crntLang);

    // Remove params for pricing requests
    if (request.url.includes('api/private/v1/pricing/')) {
      params = params.delete('companyIds').delete('multiFarm').delete('lang');
    }

    return request.clone({ params });
  }

  private catchSuccessResponse(url: string): void {
    if (!this.hasError && url && !this.isUrlOperationProgressWhiteList(url)) {
      this.operationStateService.showOperationInProgress$.next(ExecutionState.success);
    }
    // Resetta lo stato dell'errore per le richieste successive
    this.hasError = false;
  }
}
